import * as XLSX from 'xlsx';
import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { allows, unitsOf, worksOnUnit } from '../../lib/permissions';
import { formatDate, yearsSince } from '../../lib/format';
import { normalizeLinks } from '../../lib/links';
import { archiveDocuments, sendFiles, storedDocuments as storedList } from '../../lib/documents';
import { birthDateOf } from '../patients/patientModel';

// Documents filed in a patient file (Firestore collection "fichiers": patient, summary, date,
// unit). Files of the service's Dropbox are listed in `documents` as { id, name, mimeType }
// (served by /api/documents); older entries keep Dropbox links in `dropboxLinks` ({ name, link }).
// They are managed from the patient file.
export const today = () => new Date().toISOString().slice(0, 10);

export const storedDocuments = (file) => storedList(file.documents);

// Files chosen on the device, sent to the patient's folder of the service's Dropbox.
export const uploadFiles = (files, patient, onProgress) => sendFiles(files, { section: 'fichiers', patientId: patient._id }, onProgress);

export { archiveDocuments };

// An entry belongs to the unit that filed it (older entries without one: the patient's unit).
const ownsEntry = (user, file, patient) =>
  user.role === 'Admin' ||
  unitsOf(file.service || (patient && patient.services)).some((unit) => unitsOf(user.service).includes(unit));

export const fileAccess = (user, rights, patient) => ({
  canAdd: allows(rights, 'fichiers', 'modifier') && worksOnUnit(user, patient.services),
  canEdit: (file) => allows(rights, 'fichiers', 'modifier') && ownsEntry(user, file, patient),
  canDelete: (file) => allows(rights, 'fichiers', 'supprimer') && ownsEntry(user, file, patient),
});

export const saveFile = (user, patient, form, existing) => {
  const record = {
    patientId: patient._id,
    ordre: form.ordre.trim(),
    datePatient: form.datePatient,
    dropboxLinks: form.links.map(({ link, name }) => ({ link, name })),
    documents: (form.documents || []).map(({ id, name, mimeType }) => ({ id, name, mimeType: mimeType || '' })),
  };
  if (existing) {
    return updateDoc(doc(db, 'fichiers', existing.id), { ...record, updatedBy: user.name || '', updatedAt: new Date().toISOString() });
  }
  return addDoc(collection(db, 'fichiers'), {
    ...record,
    service: patient.services || user.service || '',
    statut: 'Validé',
    createdBy: user.name || '',
    createdAt: new Date().toISOString(),
  });
};

export const removeFile = (file) => deleteDoc(doc(db, 'fichiers', file.id));

// Excel list of the entries of the account's units (all units for the administration),
// with the former columns. Returns the number of rows written.
export const exportFiles = async (user, patients) => {
  const snapshot = await getDocs(collection(db, 'fichiers'));
  const byId = Object.fromEntries(patients.map((patient) => [patient._id, patient]));
  const rows = snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    .filter((file) => ownsEntry(user, file, byId[file.patientId]))
    .sort((a, b) => new Date(b.datePatient || b.createdAt || 0) - new Date(a.datePatient || a.createdAt || 0))
    .map((file) => {
      const patient = byId[file.patientId];
      const years = patient ? yearsSince(birthDateOf(patient)) : null;
      return {
        'Numéro de dossier': patient ? patient.dossierNumber : '',
        Résumé: file.ordre,
        Patient: patient ? patient.nom : '',
        Sexe: patient ? patient.genre : '',
        Diagnostic: patient ? patient.diagnostic : '',
        Age: years !== null ? years : '',
        Téléphone: patient ? patient.numeroDeTelephone : '',
        Date: formatDate(file.datePatient),
        'Nombre de documents': normalizeLinks(file.dropboxLinks).length + storedDocuments(file).length,
      };
    });
  if (!rows.length) return 0;
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, XLSX.utils.json_to_sheet(rows), 'Fichiers');
  XLSX.writeFile(book, 'fichiers_patients.xlsx');
  return rows.length;
};
